/**
 * Email validation utility for Xbox GPX Onboarding
 * Validates email format and excludes specified consumer email domains
 */

// List of blocked consumer email domains
const BLOCKED_DOMAINS = [
  "@icloud.com",
  "@outlook.com",
  "@gmail.com",
  "@hotmail.com",
  "@yahoo.com",
] as const;

export interface EmailValidationResult {
  isValid: boolean;
  errorCode?: number;
}

// Error codes constants
export const EMAIL_ERROR_CODES = {
  EMPTY_EMAIL: 0,
  INVALID_EMAIL: 1,
  INVALID_BUSINESS_EMAIL: 2,
} as const;

// Email format validation - requires proper domain with TLD
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validates email address format and domain restrictions
 * @param email - The email address to validate
 * @returns EmailValidationResult with validation status and error message
 */
export const validateEmail = (email: string): EmailValidationResult => {
  // Trim whitespace
  const trimmedEmail = email.trim();

  // Check if email is empty
  if (!trimmedEmail) {
    return {
      isValid: false,
      errorCode: EMAIL_ERROR_CODES.EMPTY_EMAIL,
    };
  }

  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      errorCode: EMAIL_ERROR_CODES.INVALID_EMAIL,
    };
  }

  // Convert to lowercase for domain comparison
  const lowercaseEmail = trimmedEmail.toLowerCase();

  // Check if email uses a blocked consumer domain
  const hasBlockedDomain = BLOCKED_DOMAINS.some(domain => lowercaseEmail.endsWith(domain));

  if (hasBlockedDomain) {
    return {
      isValid: false,
      errorCode: EMAIL_ERROR_CODES.INVALID_BUSINESS_EMAIL,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Quick validation to check if email is valid (returns boolean only)
 * @param email - The email address to validate
 * @returns boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => validateEmail(email).isValid;

/**
 * Gets the domain from an email address
 * @param email - The email address
 * @returns The domain part of the email (including @)
 */
export const getEmailDomain = (email: string): string => {
  const atIndex = email.lastIndexOf("@");
  return atIndex !== -1 ? email.substring(atIndex) : "";
};

/**
 * Checks if a domain is in the blocked list
 * @param domain - The domain to check (should include @)
 * @returns boolean indicating if domain is blocked
 */
export const isDomainBlocked = (domain: string): boolean =>
  BLOCKED_DOMAINS.includes(domain.toLowerCase() as (typeof BLOCKED_DOMAINS)[number]);

/**
 * Gets the localized error message for an error code
 * @param errorCode - The error code (0, 1, or 2)
 * @param localizedStrings - Object containing localized strings
 * @returns The appropriate error message
 */
export const getEmailErrorMessage = (
  errorCode: number,
  localizedStrings: { [key: string]: string }
): string => {
  switch (errorCode) {
    case EMAIL_ERROR_CODES.EMPTY_EMAIL:
      return localizedStrings.emailRequiredError;
    case EMAIL_ERROR_CODES.INVALID_EMAIL:
      return localizedStrings.invalidEmailAddressError;
    case EMAIL_ERROR_CODES.INVALID_BUSINESS_EMAIL:
      return localizedStrings.invalidBusinessEmailError;
    default:
      return localizedStrings.invalidEmailFormat;
  }
};
